import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// Modern, visually appealing color palette
const CHART_COLORS = [
  "#2563eb", // Blue
  "#16a34a", // Green
  "#dc2626", // Red
  "#9333ea", // Purple
  "#ea580c", // Orange
];

type ChartType = "Bar" | "SPLine" | "Area";

interface StatisticsChartProps {
  data: any[];
  xKey: string;
  yKey: string;
  title: string;
  description: string;
  chartType: ChartType;
  isLoading: boolean;
  showValue?: boolean;
  yAxisConfig?: {
    domain?: [number, number] | number[];
    tickFormatter?: (value: number) => string;
    ticks?: number[];
  };
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="text-gray-600">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-gray-900 font-semibold">
            {entry.name}: ₹{entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const formatValue = (value: number) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

// Custom formatters for specific chart types
const formatDailyValue = (value: number) => {
  return value.toLocaleString();
};

const formatWeeklyValue = (value: number) => {
  return value.toLocaleString();
};

const formatMonthlyValue = (value: number) => {
  return value.toLocaleString();
};

export function StatisticsChart({
  data,
  xKey,
  yKey,
  title,
  description,
  chartType,
  isLoading,
  showValue = true,
  yAxisConfig,
}: StatisticsChartProps) {
  const renderChart = () => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-gray-500 text-sm">No data available</p>
        </div>
      );
    }

    const commonProps = {
      width: "100%",
      height: "100%",
      data,
      margin: { top: 10, right: 20, left: 10, bottom: 40 },
    };

    switch (chartType) {
      case "Bar":
        return (
          <ResponsiveContainer {...commonProps}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey={xKey}
                tick={{ fill: '#6B7280', fontSize: 10 }}
                tickLine={{ stroke: '#6B7280' }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                type="number"
                scale="linear"
                allowDecimals={false}
                tick={{ fill: '#6B7280', fontSize: 12 }}
                tickLine={{ stroke: '#6B7280' }}
                tickFormatter={yAxisConfig?.tickFormatter || formatValue}
                domain={yAxisConfig?.domain || [0, 'auto']}
                ticks={yAxisConfig?.ticks}
                tickCount={yAxisConfig?.ticks ? yAxisConfig.ticks.length : undefined}
                allowDataOverflow={true}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey={yKey}
                name={title}
                fill={CHART_COLORS[0]}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case "SPLine":
        return (
          <ResponsiveContainer {...commonProps}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey={xKey}
                tick={{ fill: '#6B7280', fontSize: 10 }}
                tickLine={{ stroke: '#6B7280' }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                type="number"
                scale="linear"
                allowDecimals={false}
                tick={{ fill: '#6B7280', fontSize: 12 }}
                tickLine={{ stroke: '#6B7280' }}
                tickFormatter={yAxisConfig?.tickFormatter || formatValue}
                domain={yAxisConfig?.domain || [0, 'auto']}
                ticks={yAxisConfig?.ticks}
                tickCount={yAxisConfig?.ticks ? yAxisConfig.ticks.length : undefined}
                allowDataOverflow={true}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey={yKey}
                name={title}
                stroke={CHART_COLORS[0]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case "Area":
        return (
          <ResponsiveContainer {...commonProps}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey={xKey}
                tick={{ fill: '#6B7280', fontSize: 10 }}
                tickLine={{ stroke: '#6B7280' }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                type="number"
                scale="linear"
                allowDecimals={false}
                tick={{ fill: '#6B7280', fontSize: 12 }}
                tickLine={{ stroke: '#6B7280' }}
                tickFormatter={yAxisConfig?.tickFormatter || formatValue}
                domain={yAxisConfig?.domain || [0, 'auto']}
                ticks={yAxisConfig?.ticks}
                tickCount={yAxisConfig?.ticks ? yAxisConfig.ticks.length : undefined}
                allowDataOverflow={true}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area
                type="monotone"
                dataKey={yKey}
                name={title}
                fill={CHART_COLORS[0]}
                stroke={CHART_COLORS[0]}
                fillOpacity={0.2}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <Card className="w-full bg-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
      <CardHeader className="pb-1 px-3 pt-3">
        <CardTitle className="text-sm md:text-base leading-tight">{title}</CardTitle>
        <CardDescription className="text-xs md:text-sm leading-tight">{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-2 md:p-4">
        {isLoading ? (
          <div style={{ height: 220 }}>
            <Skeleton className="w-full h-full" />
          </div>
        ) : (
          <div style={{ height: 220 }}>
            {renderChart()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}